import unescape from 'lodash/unescape';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import Loader from '../../../../../common/widgets/Loader';
import { rewriteImageUrl } from '../../../helpers';
import {
  HomeCategorySource,
  SaveEveryDayCategoryItem,
} from '../../../models/Categories';
import { resources } from '../../../resources/resources';
import { CarouselDots } from '../Components/HorizontalCarousel/CarouselDots';
import { useHorizontalCarousel } from '../Components/HorizontalCarousel/useHorizontalCarousel';
import { CarouselsContext } from '../../../contexts/CarouselsProvider';

const SAVE_EVERY_DAY_CARD_WIDTH = 310;
const SAVE_EVERY_DAY_GAP = 22;
const MAX_VISIBLE_SAVE_EVERY_DAY_ITEMS = 4;

const getCategoryUrl = (item: SaveEveryDayCategoryItem) => {
  switch (item.source) {
    case HomeCategorySource.FlexibleBenefits:
      return `/flexible-benefits/${item.categoryId}`;

    case HomeCategorySource.BenefitClub:
      return `/club-benefits/${item.categoryId}`;

    default:
      return undefined;
  }
};

interface SaveEveryDayCardProps {
  item: SaveEveryDayCategoryItem;
  shopNowLabel: string;
}

const SaveEveryDayCard = ({ item, shopNowLabel }: SaveEveryDayCardProps) => {
  const url = getCategoryUrl(item);

  const content = (
    <>
      <div className="save-every-day-image">
        {item.imagePath && (
          <img
            src={rewriteImageUrl(item.imagePath)}
            alt={unescape(item.name || '')}
            draggable={false}
          />
        )}
      </div>

      <div className="save-every-day-content">
        <div className="save-every-day-name">{unescape(item.name || '')}</div>

        {item.description && (
          <div
            className="save-every-day-description"
            dangerouslySetInnerHTML={{
              __html: unescape(item.description),
            }}
          />
        )}

        <span className="save-every-day-action">{shopNowLabel}</span>
      </div>
    </>
  );

  return url ? (
    <Link className="save-every-day-item" to={url} draggable={false}>
      {content}
    </Link>
  ) : (
    <div className="save-every-day-item">{content}</div>
  );
};

export const HomeSaveEveryDay = () => {
  const { t: translate } = useTranslation();
  const { saveEveryDay, loading } = useContext(CarouselsContext);
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [sectionWidth, setSectionWidth] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const items = saveEveryDay;

  const {
    viewportRef,
    activePage,
    isDragging,
    scrollToPage,
    handlers: carouselHandlers,
  } = useHorizontalCarousel({ pageCount });

  const visibleItemCount = Math.min(
    MAX_VISIBLE_SAVE_EVERY_DAY_ITEMS,
    Math.floor(
      (sectionWidth + SAVE_EVERY_DAY_GAP) /
        (SAVE_EVERY_DAY_CARD_WIDTH + SAVE_EVERY_DAY_GAP),
    ),
  );
  const useCarousel = !!items?.length && items.length > visibleItemCount;

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const updateSectionWidth = () =>
      setSectionWidth(section.getBoundingClientRect().width);
    updateSectionWidth();

    const resizeObserver = new ResizeObserver(updateSectionWidth);
    resizeObserver.observe(section);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!useCarousel || !viewport || !items?.length) return;

    const updatePageCount = () => {
      const { clientWidth, scrollWidth } = viewport;
      const track = trackRef.current;
      const trackStyles = track ? window.getComputedStyle(track) : null;
      const trackPaddingInline = trackStyles
        ? Number.parseFloat(trackStyles.paddingLeft) +
          Number.parseFloat(trackStyles.paddingRight)
        : 0;
      const contentWidth = Math.max(0, scrollWidth - trackPaddingInline);

      if (clientWidth <= 0 || contentWidth <= 0) {
        setPageCount(1);
        return;
      }

      setPageCount(
        contentWidth > clientWidth + 1
          ? Math.max(1, Math.ceil(contentWidth / clientWidth))
          : 1,
      );
    };

    updatePageCount();

    const resizeObserver = new ResizeObserver(updatePageCount);
    resizeObserver.observe(viewport);

    return () => resizeObserver.disconnect();
  }, [items?.length, useCarousel, viewportRef]);

  if (!items?.length) {
    return null;
  }

  const cards = items.map(item => (
    <SaveEveryDayCard
      key={`${item.source}-${item.categoryId}`}
      item={item}
      shopNowLabel={translate(resources.home.action.shopNow)}
    />
  ));
  return (
    <section
      ref={sectionRef}
      className={`page-section save-every-day-section${
        useCarousel ? '' : ' save-every-day-section--static'
      }`}
    >
      <h2 className="section-title">
        <span>{translate(resources.home.title.saveEveryDay)}</span>
      </h2>

      <Loader loading={loading.saveEveryDay} loader={<Skeleton height={200} />}>
        {!!items?.length &&
          (useCarousel ? (
            <>
              <div
                ref={viewportRef}
                className={`save-every-day-viewport${
                  isDragging ? ' is-dragging' : ''
                }${
                  pageCount <= 1 ? ' save-every-day-viewport--shadow-space' : ''
                }`}
                {...carouselHandlers}
              >
                <div
                  ref={trackRef}
                  className={`save-every-day-track${
                    pageCount > 1 ? ' has-page-indicator' : ''
                  }`}
                >
                  {cards}
                </div>
              </div>

              <CarouselDots
                pageCount={pageCount}
                activePage={activePage}
                onPageChange={scrollToPage}
              />
            </>
          ) : (
            <div className="save-every-day-list">{cards}</div>
          ))}
      </Loader>
    </section>
  );
};
